import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";
import BookingClosedBanner, { useBookingConfig } from "@/components/BookingClosedBanner";
import SumUpBadge from "@/components/payments/SumUpBadge";

const BANNER = "/photos/prieure-meadow.jpg";

export default function Association() {
  const { t, locale } = useI18n();
  const config = useBookingConfig();
  const bookingOpen = !config || config.booking_open;

  const [tier, setTier] = useState("plein");
  const [freeAmount, setFreeAmount] = useState(50);
  const [memberName, setMemberName] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [tiers, setTiers] = useState({});

  useEffect(() => {
    api.get("/membership/tiers").then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  const handleMembership = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        tier,
        amount: tier === "libre" ? parseFloat(freeAmount) : undefined,
        name: memberName,
        email: memberEmail,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/membership", payload);
      window.location.href = data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur");
      setSubmitting(false);
    }
  };

  const tierItems = [
    { key: "plein", price: tiers.plein, label: t.association.tierLabels.plein, note: "" },
    { key: "couple", price: tiers.couple, label: t.association.tierLabels.couple, note: t.association.tierLabels.coupleNote },
    { key: "reduit", price: tiers.reduit, label: t.association.tierLabels.reduit, note: t.association.tierLabels.reduitNote },
    { key: "scouts_guides", price: tiers.scouts_guides, label: t.association.tierLabels.scouts_guides, note: t.association.tierLabels.scoutsGuidesNote },
    { key: "paroissiens_avranches", price: tiers.paroissiens_avranches, label: t.association.tierLabels.paroissiens_avranches, note: t.association.tierLabels.paroissiensAvranchesNote },
    { key: "libre", price: null, label: t.association.tierLabels.libre, note: t.association.tierLabels.libreNote },
  ];

  return (
    <div data-testid="association-page">
      <section className="relative" style={{ height: "42vh", minHeight: 320 }}>
        <img src={BANNER} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.72rem] text-white/90 mb-4">{t.association.kicker}</div>
          <h1 className="font-display text-5xl sm:text-7xl text-white">{t.association.title}</h1>
        </div>
      </section>

      {/* Official crest of the Friends of the Priory */}
      <section
        data-testid="association-crest"
        className="py-16 sm:py-24"
        style={{ background: "var(--parchment)" }}
      >
        <div className="container-prieure flex flex-col items-center text-center">
          <img
            src="/logo-prieure.png"
            alt={locale === "fr"
              ? "Sceau des Amis du Prieuré saint Léonard"
              : "Crest of the Friends of the Priory of Saint Léonard"}
            data-testid="association-crest-img"
            className="w-40 sm:w-52 h-auto"
            style={{ objectFit: "contain", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))" }}
          />
          <div
            className="font-caps text-[0.7rem] mt-6"
            style={{ color: "var(--gold)", letterSpacing: "0.3em" }}
          >
            {locale === "fr" ? "Sceau de l'association" : "Crest of the association"}
          </div>
          <div className="font-display italic text-xs sm:text-sm mt-2" style={{ color: "var(--ink-2)" }}>
            {locale === "fr" ? "Les" : "The"}
          </div>
          <div className="font-display text-2xl sm:text-3xl mt-1" style={{ color: "var(--ink)" }}>
            {locale === "fr"
              ? "Amis du Prieuré saint Léonard"
              : "Friends of the Priory of Saint Léonard"}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="container-prieure max-w-4xl">
          <Ornament className="mb-8" />
          <p className="font-display italic text-2xl mb-6">{t.association.mission}</p>
          <ul className="space-y-4 font-body text-lg" style={{ color: "var(--ink-2)" }}>
            {t.association.missions.map((m, i) => (
              <li key={i} className="flex gap-3">
                <span style={{ color: "var(--cinnabar)" }}>✜</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Membership */}
      <section id="adhesion" className="py-20" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure">
          <h2 className="font-display text-4xl sm:text-5xl mb-3">{t.association.tiersTitle}</h2>
          <p className="font-body mb-12 max-w-2xl" style={{ color: "var(--ink-2)" }}>
            {t.association.membershipNote}
          </p>

          <BookingClosedBanner />

          <form onSubmit={handleMembership} data-testid="membership-form"
                className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tierItems.map((it) => (
                <label
                  key={it.key}
                  data-testid={`tier-${it.key}`}
                  className={`cursor-pointer p-6 border bg-white transition ${
                    tier === it.key ? "ring-2" : ""
                  }`}
                  style={{
                    borderColor: tier === it.key ? "var(--cinnabar)" : "var(--border)",
                  }}
                >
                  <input type="radio" name="tier" value={it.key} checked={tier === it.key}
                         onChange={() => setTier(it.key)} className="sr-only" />
                  <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--gold)" }}>
                    {it.label}
                  </div>
                  {it.price != null ? (
                    <div className="font-display text-4xl mb-2">{it.price}{t.common.euro}</div>
                  ) : (
                    <div className="font-display text-2xl italic mb-2">— — —</div>
                  )}
                  {it.note && <p className="font-body text-sm" style={{ color: "var(--muted)" }}>{it.note}</p>}
                </label>
              ))}
            </div>

            <div className="lg:col-span-5 illuminated-border bg-white">
              <div className="label-caps mb-4" style={{ color: "var(--cinnabar)" }}>{t.association.formTitle}</div>

              {tier === "libre" && (
                <>
                  <label className="label-caps">{t.association.freeAmount}</label>
                  <input
                    data-testid="member-amount"
                    type="number" min="1" step="1" value={freeAmount}
                    onChange={(e) => setFreeAmount(e.target.value)}
                    className="input-classic mt-1 mb-4"
                  />
                </>
              )}

              <label className="label-caps">{t.association.fullName}</label>
              <input data-testid="member-name" required value={memberName}
                     onChange={(e) => setMemberName(e.target.value)}
                     className="input-classic mt-1 mb-4" />

              <label className="label-caps">{t.association.email}</label>
              <input data-testid="member-email" required type="email" value={memberEmail}
                     onChange={(e) => setMemberEmail(e.target.value)}
                     className="input-classic mt-1 mb-6" />

              {error && <p className="font-body text-sm mb-3" style={{ color: "var(--cinnabar)" }}>{String(error)}</p>}

              <button data-testid="member-submit" type="submit" disabled={submitting || !bookingOpen}
                      className="btn btn-primary w-full">
                {!bookingOpen ? (locale === "fr" ? "Bientôt disponible" : "Coming soon") : (submitting ? t.common.loading : t.association.proceedJoin)}
              </button>
              <div className="flex justify-center mt-4">
                <SumUpBadge />
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Mécénat call-out — replaces the former "Projets / chantiers" section */}
      <section data-testid="mecenat-callout" className="py-16" style={{ background: "var(--vellum)" }}>
        <div className="container-prieure max-w-3xl text-center">
          <div className="font-caps text-[0.72rem] mb-3" style={{ color: "var(--cinnabar)", letterSpacing: "0.28em" }}>
            {locale === "fr" ? "AUTRE FAÇON DE SOUTENIR" : "ANOTHER WAY TO SUPPORT US"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mb-4">
            {locale === "fr" ? "Faire un don au prieuré" : "Donate to the priory"}
          </h2>
          <Ornament />
          <p className="font-body text-lg mt-6 mb-8" style={{ color: "var(--ink-2)" }}>
            {locale === "fr"
              ? "Soutenez l'entretien de la chapelle, la transmission du patrimoine et la préparation des Fêtes de saint Léonard 2027. Chaque don ouvre droit à un reçu fiscal — réduction d'impôt de 66 % pour les particuliers, 60 % pour les entreprises."
              : "Support the upkeep of the chapel, the transmission of our heritage and the preparation for the 2027 Feasts of Saint Léonard. Each donation entitles you to a tax reduction — 66 % for individuals, 60 % for companies."}
          </p>
          <Link to="/mecenat" data-testid="cta-mecenat" className="btn btn-primary inline-block"
                style={{ padding: "0.85rem 2.2rem" }}>
            {locale === "fr" ? "Découvrir le mécénat" : "Discover patronage"}
          </Link>
        </div>
      </section>
    </div>
  );
}
